import { Box, Stack } from "@mui/material"
import { MoreHorizOutlined } from "@mui-symbols-material/w300"
import type { KeyboardEvent, ReactElement, ReactNode } from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { AerosTag } from "./AerosTag"

export interface AerosTagData {
  key: number | string
  label: string
  value?: number | string
  icon?: ReactElement
}

export interface AerosTagStackProps {
  disabled?: boolean
  tags: AerosTagData[]
  onDelete?: (tagToDelete: AerosTagData) => void
  children?: ReactNode
  className?: string
  shouldWrap?: boolean
}

export const AerosTagStack = ({
  disabled = false,
  tags = [],
  onDelete,
  children,
  className,
  shouldWrap = false,
}: AerosTagStackProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLDivElement>(null)
  const [visibleTags, setVisibleTags] = useState<AerosTagData[]>([])
  const [isOverflowing, setIsOverflowing] = useState(false)

  const updateVisibleTags = useCallback(() => {
    if (!measureRef.current || !containerRef.current) return

    const containerWidth = containerRef.current.clientWidth - 40 // Reserve space for overflow indicator
    const gapWidth = 8 // gap-2 = 0.5rem = 8px
    let totalWidth = 0
    const visibleTagsArray: AerosTagData[] = []

    Array.from(measureRef.current.children).forEach((tag, index) => {
      const tagWidth = (tag as HTMLElement).offsetWidth
      const widthWithGap = totalWidth > 0 ? tagWidth + gapWidth : tagWidth

      if (totalWidth + widthWithGap <= containerWidth) {
        visibleTagsArray.push(tags[index])
        totalWidth += widthWithGap
      }
    })

    setVisibleTags(visibleTagsArray)
    setIsOverflowing(visibleTagsArray.length < tags.length)
  }, [tags])

  useLayoutEffect(() => {
    updateVisibleTags()
  }, [tags, updateVisibleTags])

  const handleDelete = useCallback(
    (tagToDelete: AerosTagData) => {
      if (disabled) return

      onDelete?.(tagToDelete)
    },
    [disabled, onDelete]
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent, tag: AerosTagData) => {
      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault()
        event.stopPropagation()
        handleDelete(tag)
      }
    },
    [handleDelete]
  )

  const renderTag = (tag: AerosTagData) => (
    <AerosTag
      key={tag.key}
      icon={tag.icon}
      label={tag.label}
      disabled={disabled}
      onDelete={disabled ? undefined : () => handleDelete(tag)}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => handleKeyDown(e, tag)}
    />
  )

  return (
    <Stack
      ref={containerRef}
      direction='row'
      className={twMerge("relative grow items-center gap-2", shouldWrap ? "flex-wrap" : "", className)}
    >
      {shouldWrap ? (
        tags.map(renderTag)
      ) : (
        <>
          {visibleTags.map(renderTag)}
          {isOverflowing && (
            <AerosTag
              label={<MoreHorizOutlined className='size-4' />}
              sx={{ "& .MuiChip-label": { padding: "0 0.25rem" } }}
            />
          )}
        </>
      )}
      {children && <Box className='flex-none'>{children}</Box>}
      <Box className='invisible absolute' ref={measureRef}>
        {tags.map(renderTag)}
      </Box>
    </Stack>
  )
}

AerosTagStack.displayName = "AerosTagStack"
